import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { Package, ExternalLink, TrendingUp, TrendingDown, Scale, BarChart3 } from "lucide-react";
import FreshnessDot from "./FreshnessDot";
import ETFHoldingsBenchmarkChart from "./ETFHoldingsBenchmarkChart";
import ETFHoldingsMomentumMatrix from "./ETFHoldingsMomentumMatrix";

/**
 * Displays ETF holdings at the bottom of the Stock Analysis page.
 * Shows live "Today %", per-holding weighted contribution, and an ETF vs
 * constituents divergence signal (with 5Y backtest).
 *
 * Props:
 *   ticker: string
 *   onHoldingClick?: (symbol: string) => void
 */
export default function ETFHoldings({ ticker, onHoldingClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Selected timeframe — 1d uses the existing live intraday quotes endpoint,
  // anything else hits /etf-holdings-period-returns for the window of trading-days.
  const [timeframe, setTimeframe] = useState("1d");
  const [periodLoading, setPeriodLoading] = useState(false);

  // Sort state — null sortKey means use API's natural order (by rank/weight).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  // Live quotes for each holding
  const [quotes, setQuotes] = useState({});
  const [etfChange, setEtfChange] = useState(null);
  const [weightedPct, setWeightedPct] = useState(null);
  const [divergence, setDivergence] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [quotesTs, setQuotesTs] = useState(null);

  // 5Y backtest
  const [backtest, setBacktest] = useState(null);
  const [btLoading, setBtLoading] = useState(false);

  // Fetch holdings metadata
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    const fetchHoldings = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/stock/etf-holdings/${ticker}`, { timeout: 25000 });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHoldings();
    return () => { cancelled = true; };
  }, [ticker]);

  // Live intraday quotes + auto-refresh 30s.  Only runs when `timeframe == "1d"`.
  useEffect(() => {
    if (!ticker || !data?.is_etf || timeframe !== "1d") return;
    let cancelled = false;
    const fetchQuotes = async () => {
      try {
        const res = await axios.get(`${API}/stock/etf-holdings-quotes/${ticker}`, { timeout: 25000 });
        if (cancelled) return;
        setQuotes(res.data?.quotes || {});
        setEtfChange(res.data?.etf_change_pct ?? null);
        setWeightedPct(res.data?.weighted_change_pct ?? null);
        setDivergence(res.data?.divergence_pct ?? null);
        setCoverage(res.data?.coverage_weight_pct ?? null);
        setQuotesTs(Date.now());
      } catch { /* keep last */ }
    };
    fetchQuotes();
    const id = setInterval(fetchQuotes, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ticker, data?.is_etf, timeframe]);

  // Multi-day period returns (5d / 10d / 20d / 50d / 100d / 200d / mtd / ytd / 1y).
  // Hits the new /etf-holdings-period-returns endpoint and replaces the same
  // quote-shaped state so the existing table just works.
  useEffect(() => {
    if (!ticker || !data?.is_etf || timeframe === "1d") return;
    let cancelled = false;
    const fetchPeriod = async () => {
      setPeriodLoading(true);
      try {
        const res = await axios.get(
          `${API}/stock/etf-holdings-period-returns/${ticker}`,
          { params: { timeframe }, timeout: 60000 });
        if (cancelled) return;
        setQuotes(res.data?.returns || {});
        setEtfChange(res.data?.etf_change_pct ?? null);
        setWeightedPct(res.data?.weighted_change_pct ?? null);
        setDivergence(res.data?.divergence_pct ?? null);
        setCoverage(res.data?.coverage_weight_pct ?? null);
        setQuotesTs(Date.now());
      } catch { /* keep last */ } finally {
        if (!cancelled) setPeriodLoading(false);
      }
    };
    fetchPeriod();
    return () => { cancelled = true; };
  }, [ticker, data?.is_etf, timeframe]);

  // 5Y backtest (one-time per ticker, 12h cached backend)
  useEffect(() => {
    if (!ticker || !data?.is_etf) return;
    let cancelled = false;
    const fetchBt = async () => {
      setBtLoading(true);
      try {
        const res = await axios.get(`${API}/stock/etf-divergence-backtest/${ticker}`, { timeout: 60000 });
        if (!cancelled) setBacktest(res.data);
      } catch {
        if (!cancelled) setBacktest(null);
      } finally {
        if (!cancelled) setBtLoading(false);
      }
    };
    fetchBt();
    return () => { cancelled = true; };
  }, [ticker, data?.is_etf]);

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-4" data-testid="etf-holdings-loading">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Package className="w-4 h-4 animate-pulse" />
          Checking if {ticker} is an ETF…
        </div>
      </div>
    );
  }
  if (!data || !data.is_etf) return null;

  const holdings = data.holdings || [];

  // Helper: extract sort value for each row by sort key. `change_pct`
  // and `contribution_pct` come from the per-symbol quotes object.
  const valueFor = (h, key) => {
    if (key === "weight_pct")       return h.weight_pct ?? 0;
    if (key === "shares")           return h.shares ?? 0;
    if (key === "change_pct")       return quotes[h.symbol]?.change_pct ?? quotes[h.symbol]?.change ?? null;
    if (key === "contribution_pct") return quotes[h.symbol]?.contribution_pct ?? null;
    return 0;
  };

  // Sort the displayed holdings.  Default sort is by weight desc (the API's
  // natural order); user can click a column header to re-sort.
  let sortedHoldings = holdings;
  if (sortKey) {
    sortedHoldings = [...holdings].sort((a, b) => {
      const va = valueFor(a, sortKey);
      const vb = valueFor(b, sortKey);
      // Push nulls to the end regardless of direction
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return sortDir === "desc" ? vb - va : va - vb;
    });
  }
  const visible = expanded ? sortedHoldings : sortedHoldings.slice(0, 10);
  const topWeight = holdings.slice(0, 10).reduce((s, h) => s + (h.weight_pct || 0), 0);

  // Header click toggles direction when the same column is clicked,
  // otherwise switches column and defaults to descending (highest first).
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };
  const sortIcon = (key) =>
    sortKey !== key ? <span className="text-slate-600">↕</span>
      : sortDir === "desc" ? <span className="text-cyan-300">▼</span>
                           : <span className="text-cyan-300">▲</span>;

  const fmtPct = (n, digits = 2) => {
    if (n === null || n === undefined || isNaN(n)) return "—";
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(digits)}%`;
  };
  const fmtPP = (n, digits = 3) => {
    if (n === null || n === undefined || isNaN(n)) return "—";
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(digits)}pp`;
  };
  const pctColor = (n) => {
    if (n === null || n === undefined || isNaN(n)) return "text-gray-500";
    if (n > 0.01) return "text-green-400";
    if (n < -0.01) return "text-red-400";
    return "text-gray-400";
  };
  const divergenceLabel = (d) => {
    if (d === null || d === undefined) return null;
    if (d > 0.02) return "ETF UNDER-PRICED";
    if (d < -0.02) return "ETF OVER-PRICED";
    return "ETF FAIRLY PRICED";
  };

  return (
    <div
      className="rounded-lg border border-indigo-900/40 bg-gradient-to-br from-indigo-950/30 via-gray-900/60 to-gray-900/50 p-4 space-y-4"
      data-testid="etf-holdings-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Package className="w-5 h-5 text-indigo-400" />
          <h3 className="text-lg font-semibold text-white">{ticker} ETF Holdings</h3>
          <span className="px-2 py-0.5 rounded bg-indigo-900/60 text-indigo-200 text-[10px] font-mono" data-testid="etf-holdings-count">
            {data.holdings_count} holdings
          </span>
          {topWeight > 0 && (
            <span className="text-[10px] text-gray-500 font-mono">Top 10 = {topWeight.toFixed(1)}%</span>
          )}
          {quotesTs && <FreshnessDot timestamp={quotesTs} />}
        </div>
        {data.updated && (
          <span className="text-[10px] text-gray-500 font-mono">As of {data.updated}</span>
        )}
      </div>

      {/* Timeframe toggle — switches every return column to a multi-trading-day window
          so we can use the ETF as a benchmark and spot over/under-performing stocks. */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="etf-holdings-timeframe-row">
        <span className="text-[10px] uppercase tracking-wider text-slate-300 font-bold">Window</span>
        {[
          { key: "1d",   label: "1D"   },
          { key: "5d",   label: "5D"   },
          { key: "10d",  label: "10D"  },
          { key: "20d",  label: "20D"  },
          { key: "50d",  label: "50D"  },
          { key: "100d", label: "100D" },
          { key: "200d", label: "200D" },
          { key: "mtd",  label: "MTD"  },
          { key: "ytd",  label: "YTD"  },
          { key: "1y",   label: "1Y"   },
        ].map(t => {
          const on = timeframe === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTimeframe(t.key)}
              data-testid={`etf-holdings-tf-${t.key}`}
              className={`px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                ${on
                  ? "bg-indigo-600/30 border-indigo-400 text-white"
                  : "bg-slate-900 border-slate-700 text-slate-300 hover:text-white hover:border-slate-500"}`}
            >
              {t.label}
            </button>
          );
        })}
        {periodLoading && (
          <span className="text-[10px] text-indigo-300 italic ml-1">loading window…</span>
        )}
      </div>

      {/* ETF vs Weighted divergence panel */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="etf-divergence-panel">
        <MetricTile
          label={`ETF ${timeframe === "1d" ? "Today" : timeframe.toUpperCase()}`}
          value={fmtPct(etfChange)}
          color={pctColor(etfChange)}
          icon={etfChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          testid="etf-tile-actual"
        />
        <MetricTile
          label="Weighted from Holdings"
          value={fmtPct(weightedPct)}
          color={pctColor(weightedPct)}
          sub={coverage !== null ? `${coverage.toFixed(0)}% coverage` : null}
          icon={<Scale className="w-3 h-3" />}
          testid="etf-tile-weighted"
        />
        <MetricTile
          label="Divergence (W − ETF)"
          value={fmtPct(divergence, 3)}
          color={pctColor(divergence)}
          sub={divergenceLabel(divergence)}
          icon={<BarChart3 className="w-3 h-3" />}
          highlight
          testid="etf-tile-divergence"
        />
        <MetricTile
          label="5Y Z-Score"
          value={backtest?.today_z_score !== null && backtest?.today_z_score !== undefined
            ? `${backtest.today_z_score > 0 ? "+" : ""}${backtest.today_z_score}σ`
            : btLoading ? "…" : "—"}
          color={
            backtest?.today_z_score === null || backtest?.today_z_score === undefined
              ? "text-gray-500"
              : Math.abs(backtest.today_z_score) > 2
              ? "text-amber-300"
              : Math.abs(backtest.today_z_score) > 1
              ? "text-blue-300"
              : "text-gray-300"
          }
          sub={backtest?.samples ? `vs ${backtest.samples}d history` : null}
          meaning={(() => {
            const z = backtest?.today_z_score;
            if (z === null || z === undefined) return null;
            const a = Math.abs(z);
            const dir = z > 0 ? "ahead of" : "behind";
            // Approx % of trading days that fall within ±|z| σ of the mean
            // (normal-distribution lookup): 0.5σ≈38%, 1σ≈68%, 1.5σ≈87%, 2σ≈95%, 2.5σ≈99%, 3σ≈99.7%
            const tail = a >= 3 ? "0.3%" : a >= 2.5 ? "1%" : a >= 2 ? "5%" : a >= 1.5 ? "13%" : a >= 1 ? "32%" : a >= 0.5 ? "62%" : "75%";
            if (a < 0.5) {
              return `Today's gap between this ETF and its holdings is normal — basically average versus the last 5 years.`;
            }
            if (a < 1) {
              return `Today is mildly unusual. The ETF is ${dir} its holdings by a touch more than typical, but still well within the everyday range.`;
            }
            if (a < 2) {
              return `Today is notably unusual. The ETF is ${dir} its holdings — only about ${tail} of trading days in the last 5 years saw a wider gap.`;
            }
            if (a < 3) {
              return `Today is rare. The ETF is ${dir} its holdings by a wide margin — only about ${tail} of trading days in the last 5 years saw a gap this big. Mean-reversion bets are statistically favored when this happens.`;
            }
            return `Today is extreme — a 3+ standard-deviation event. The ETF is ${dir} its holdings by a margin you'd expect on roughly ${tail} of all trading days. Watch for a strong snap-back.`;
          })()}
          testid="etf-tile-zscore"
        />
      </div>

      {/* Holdings Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
              <th className="text-left py-2 px-2 font-semibold w-10">#</th>
              <th className="text-left py-2 px-2 font-semibold w-24">Ticker</th>
              <th className="text-left py-2 px-2 font-semibold">Name</th>
              <th className="text-right py-2 px-2 font-semibold cursor-pointer hover:text-cyan-200 select-none"
                  onClick={() => handleSort("weight_pct")}
                  data-testid="etf-holdings-sort-weight">
                Weight {sortIcon("weight_pct")}
              </th>
              <th className="text-right py-2 px-2 font-semibold cursor-pointer hover:text-cyan-200 select-none"
                  onClick={() => handleSort("change_pct")}
                  data-testid="etf-holdings-sort-change">
                {timeframe === "1d" ? "Today %" : `${timeframe.toUpperCase()} %`} {sortIcon("change_pct")}
              </th>
              <th className="text-right py-2 px-2 font-semibold cursor-pointer hover:text-cyan-200 select-none"
                  onClick={() => handleSort("contribution_pct")}
                  title={`Weight × Change ÷ 100 — this stock's push on the ETF over ${timeframe.toUpperCase()}, in percentage points`}
                  data-testid="etf-holdings-sort-contrib">
                Contribution {sortIcon("contribution_pct")}
              </th>
              <th className="text-right py-2 px-2 font-semibold hidden sm:table-cell cursor-pointer hover:text-cyan-200 select-none"
                  onClick={() => handleSort("shares")}
                  data-testid="etf-holdings-sort-shares">
                Shares {sortIcon("shares")}
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((h) => {
              const clickable = !!h.symbol;
              const quoteKey = h.symbol || h.foreign_symbol;
              const q = quoteKey ? quotes[quoteKey] : null;
              const chg = q?.change_pct;
              const contrib = q?.contribution_pct;
              const isNative = q?.source === "yahoo_native";
              return (
                <tr
                  key={h.rank || h.symbol}
                  data-testid={`etf-holding-row-${h.symbol || h.rank}`}
                  className={`border-b border-gray-800/50 ${clickable ? 'hover:bg-gray-800/40 cursor-pointer' : ''}`}
                  onClick={() => { if (clickable && onHoldingClick) onHoldingClick(h.symbol); }}
                >
                  <td className="py-2 px-2 text-gray-500 font-mono text-[11px]">{h.rank}</td>
                  <td className="py-2 px-2">
                    {clickable ? (
                      <div className="inline-flex flex-col">
                        <span className="inline-flex items-center gap-1 font-mono font-bold text-cyan-300 hover:text-cyan-200">
                          {h.symbol}
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </span>
                        {h.foreign_symbol && h.foreign_symbol !== h.symbol && (
                          <span className="text-[9px] text-gray-500 font-mono" title={`Local exchange: ${h.foreign_symbol}`}>
                            {h.foreign_symbol}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="inline-flex flex-col">
                        <span className="font-mono text-gray-500 italic">no US ADR</span>
                        {h.foreign_symbol && (
                          <span className="text-[9px] text-gray-600 font-mono">{h.foreign_symbol}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-gray-200">{h.name || '—'}</td>
                  <td className="py-2 px-2 text-right font-mono text-amber-300 font-bold">
                    {h.weight_pct.toFixed(2)}%
                  </td>
                  <td className={`py-2 px-2 text-right font-mono font-semibold ${pctColor(chg)}`}
                      data-testid={`etf-holding-change-${h.symbol || h.rank}`}
                      title={isNative ? `Native quote from ${q?.native_symbol || h.foreign_symbol}` : "US quote from MSN Finance"}>
                    {fmtPct(chg)}
                    {isNative && <span className="ml-1 text-[8px] text-indigo-400 font-normal align-top">◌</span>}
                  </td>
                  <td className={`py-2 px-2 text-right font-mono ${pctColor(contrib)}`}
                      data-testid={`etf-holding-contribution-${h.symbol || h.rank}`}>
                    {fmtPP(contrib)}
                  </td>
                  <td className="py-2 px-2 text-right font-mono text-gray-400 hidden sm:table-cell">
                    {h.shares ? h.shares.toLocaleString() : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {holdings.length > 10 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="etf-holdings-expand"
          className="w-full py-2 text-xs font-semibold text-indigo-300 hover:text-indigo-200 bg-indigo-900/30 hover:bg-indigo-800/40 rounded transition-colors"
        >
          {expanded ? `Show top 10` : `Show all ${holdings.length} holdings`}
        </button>
      )}

      {/* Momentum Matrix — wide sortable table showing every constituent
          with industry, market cap, weight and the full returns matrix
          (1D/5D/10D/20D/50D/100D/200D/1Y/3Y/5Y) side-by-side. */}
      <ETFHoldingsMomentumMatrix ticker={ticker} />

      {/* Multi-Benchmark Comparison chart for ETF constituents — toggle any
          holding on/off and compare against the ETF + SPY/QQQ/DIA. Uses the
          same trading-day window selector as the table above. */}
      <ETFHoldingsBenchmarkChart
        etfTicker={ticker}
        holdings={holdings}
        timeframe={timeframe}
      />

      {/* 5Y Backtest Panel */}
      {backtest && backtest.is_etf && backtest.stats && (
        <BacktestPanel bt={backtest} ticker={ticker} />
      )}
      {btLoading && !backtest && (
        <div className="text-[11px] text-gray-500 italic">Running 5-year divergence backtest…</div>
      )}

      <p className="text-[10px] text-gray-500 leading-snug">
        Contribution = holding weight × today's change ÷ 100 (percentage-point push on ETF).
        <span className="text-indigo-400 mx-1">◌</span> indicates a native foreign-exchange quote (Yahoo v8) used because the US ADR was unavailable or stale. Divergence is compared against {backtest?.years || 5}Y of daily history to flag statistically unusual days.
      </p>
    </div>
  );
}

/** Small KPI tile for the divergence panel */
function MetricTile({ label, value, color = "text-gray-200", sub, icon, highlight, testid, meaning }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-3 ${
        highlight ? "border-indigo-700/60 bg-indigo-950/30" : "border-gray-800 bg-gray-900/60"
      }`}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {icon}
        {label}
      </div>
      <div className={`mt-1 font-mono text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
      {meaning && (
        <div className="text-[10px] text-gray-400 mt-1.5 leading-tight italic">
          {meaning}
        </div>
      )}
    </div>
  );
}

/** 5-Year Divergence Backtest Panel */
function BacktestPanel({ bt, ticker }) {
  const s = bt.stats || {};
  const p = bt.predictive || {};
  const baseUp = p.base_up_rate !== null ? p.base_up_rate * 100 : null;
  const upAfterPos = p.up_rate_after_positive_divergence !== null ? p.up_rate_after_positive_divergence * 100 : null;
  const downAfterNeg = p.down_rate_after_negative_divergence !== null ? p.down_rate_after_negative_divergence * 100 : null;

  const edgePos = upAfterPos !== null && baseUp !== null ? upAfterPos - baseUp : null;
  const edgeNeg = downAfterNeg !== null && baseUp !== null ? downAfterNeg - (100 - baseUp) : null;

  // Verdict
  let verdict = "No edge — divergence is noise";
  let verdictColor = "text-gray-400";
  const absCorr = Math.abs(p.correlation_next_day || 0);
  const maxEdge = Math.max(Math.abs(edgePos || 0), Math.abs(edgeNeg || 0));
  if (absCorr >= 0.05 || maxEdge >= 3) {
    verdict = "Weak-to-moderate edge";
    verdictColor = "text-blue-300";
  }
  if (absCorr >= 0.1 || maxEdge >= 5) {
    verdict = "Meaningful predictive edge";
    verdictColor = "text-green-400";
  }

  return (
    <div
      className="rounded-md border border-indigo-800/40 bg-gradient-to-br from-indigo-950/20 to-gray-900/40 p-3 space-y-3"
      data-testid="etf-divergence-backtest"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-400" />
          <h4 className="text-sm font-semibold text-white">{ticker} Divergence Backtest · {bt.years}Y</h4>
          <span className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 text-[9px] font-mono">
            {bt.samples} days · {bt.used_holdings} holdings · {bt.coverage_weight_pct}% weight
          </span>
        </div>
        <span className={`text-[11px] font-semibold ${verdictColor}`} data-testid="etf-backtest-verdict">
          {verdict}
        </span>
      </div>

      {/* Distribution stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
        <StatBox label="5Y Mean Divergence" value={`${s.mean_pct > 0 ? "+" : ""}${s.mean_pct}%`} />
        <StatBox label="Std Dev" value={`${s.std_pct}%`} />
        <StatBox label="Typical Range (±1σ)" value={`${s.p25_pct}% to +${s.p75_pct}%`} />
        <StatBox label="Historical Extremes" value={`${s.min_pct}% to +${s.max_pct}%`} />
      </div>

      {/* Predictive stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
        <PredictiveBox
          title="Next-Day Correlation"
          main={p.correlation_next_day !== null ? p.correlation_next_day.toFixed(3) : "—"}
          hint={absCorr < 0.05 ? "near zero — signal is noise" : absCorr < 0.15 ? "weak but non-zero" : "notable correlation"}
          color={absCorr >= 0.15 ? "text-green-400" : absCorr >= 0.05 ? "text-blue-300" : "text-gray-400"}
        />
        <PredictiveBox
          title="When Divergence > 0"
          main={upAfterPos !== null ? `${upAfterPos.toFixed(1)}% up next day` : "—"}
          hint={edgePos !== null ? `vs ${baseUp.toFixed(1)}% base (${edgePos >= 0 ? "+" : ""}${edgePos.toFixed(1)}pp edge)` : ""}
          color={edgePos !== null && edgePos >= 3 ? "text-green-400" : edgePos !== null && edgePos >= 1 ? "text-blue-300" : "text-gray-400"}
          sub={`n=${p.n_positive_days}`}
        />
        <PredictiveBox
          title="When Divergence < 0"
          main={downAfterNeg !== null ? `${downAfterNeg.toFixed(1)}% down next day` : "—"}
          hint={edgeNeg !== null ? `vs ${(100 - baseUp).toFixed(1)}% base (${edgeNeg >= 0 ? "+" : ""}${edgeNeg.toFixed(1)}pp edge)` : ""}
          color={edgeNeg !== null && edgeNeg >= 3 ? "text-green-400" : edgeNeg !== null && edgeNeg >= 1 ? "text-blue-300" : "text-gray-400"}
          sub={`n=${p.n_negative_days}`}
        />
      </div>

      {/* Recent 30-day divergence sparkline (simple inline SVG) */}
      {bt.recent_divergence && bt.recent_divergence.length > 5 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
            Last {bt.recent_divergence.length} days · Divergence
          </div>
          <Sparkline data={bt.recent_divergence} />
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded border border-gray-800 bg-gray-900/60 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-gray-200 font-semibold">{value}</div>
    </div>
  );
}

function PredictiveBox({ title, main, hint, sub, color = "text-gray-200" }) {
  return (
    <div className="rounded border border-gray-800 bg-gray-900/60 px-3 py-2">
      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">{title}</div>
      <div className={`font-mono text-sm font-bold ${color}`}>{main}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
      {sub && <div className="text-[9px] text-gray-600 font-mono mt-0.5">{sub}</div>}
    </div>
  );
}

function Sparkline({ data }) {
  if (!data || data.length < 2) return null;
  const values = data.map((d) => d.divergence_pct);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 0.001);
  const w = 100, h = 40;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(" ");
  const zeroY = h - ((0 - min) / range) * h;
  const last = values[values.length - 1];
  const lastColor = last > 0.02 ? "#4ade80" : last < -0.02 ? "#f87171" : "#9ca3af";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10" preserveAspectRatio="none" data-testid="etf-divergence-sparkline">
      {zeroY >= 0 && zeroY <= h && (
        <line x1="0" y1={zeroY} x2={w} y2={zeroY} stroke="#374151" strokeDasharray="2 2" strokeWidth="0.5" />
      )}
      <polyline fill="none" stroke={lastColor} strokeWidth="1.2" points={points} />
      <circle cx={w} cy={h - ((last - min) / range) * h} r="1.4" fill={lastColor} />
    </svg>
  );
}
